import {
  LoadingOverlayContextType,
  ToastColor,
  ToastPlacement,
  ToastProps,
} from "./../utils/types";
import React, { createContext, useContext, useState, useCallback } from "react";
import { Outlet } from "react-router-dom";

const LoadingOverlayContext = createContext<
  | (LoadingOverlayContextType & {
      showToast: (toast: Omit<ToastProps, "id">) => void;
    })
  | undefined
>(undefined);

export const useLoadingOverlay = () => {
  const ctx = useContext(LoadingOverlayContext);
  if (!ctx)
    throw new Error(
      "useLoadingOverlay must be used within LoadingOverlayProvider"
    );
  return ctx;
};

const toastPlacementClasses: Record<ToastPlacement, string> = {
  "top-left": "top-0 start-0",
  "top-right": "top-0 end-0",
  "bottom-left": "bottom-0 start-0",
  "bottom-right": "bottom-0 end-0",
};

const toastColorClasses: Record<ToastColor, string> = {
  success: "bg-success text-white",
  danger: "bg-danger text-white",
};

const Toast: React.FC<ToastProps & { onClose: () => void }> = ({
  color,
  closeButton,
  title,
  onClose,
}) => (
  <div
    className={`d-flex align-items-center shadow px-4 py-2 rounded mb-2 ${toastColorClasses[color]}`}
    style={{ minWidth: "220px" }}
    role="alert"
  >
    <div className="flex-grow-1">{title}</div>
    {closeButton && (
      <button
        onClick={onClose}
        className="ms-4 text-white fs-5 fw-bold focus:outline-none"
        aria-label="Close"
        style={{ background: "transparent", border: "none" }}
      >
        ×
      </button>
    )}
  </div>
);

export const LoadingOverlayProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const [loading, setLoading] = useState<number>(0);
  const [data, setData] = useState<any>({});
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const [toastId, setToastId] = useState(0);

  const isLoading = React.useMemo(() => loading > 0, [loading]);

  const loadingIncrement = () => setLoading((prev) => prev + 1);
  const loadingDecrement = () => setLoading((prev) => Math.max(prev - 1, 0));

  const showToast = useCallback(
    (toast: Omit<ToastProps, "id">) => {
      const newId = toastId + 1;
      setToastId(newId);
      const fullToast: ToastProps = {
        ...toast,
        id: newId,
        timeout: toast.timeout || 2500,
        timed: toast.timed !== undefined ? toast.timed : true,
      };

      if (fullToast.timed) {
        setTimeout(() => {
          setToasts((ts) => ts.filter((t) => t.id !== newId));
        }, fullToast.timeout);
      }

      setToasts((ts) => [...ts, fullToast]);
      return newId;
    },
    [toastId]
  );

  // Group toasts by placement
  const getToastsByPlacement = (placement: ToastPlacement) =>
    toasts.filter((t) => t.placement === placement);

  return (
    <LoadingOverlayContext.Provider
      value={{
        setData,
        loadingIncrement,
        loadingDecrement,
        data,
        loading,
        showToast,
      }}
    >
      <div className="position-relative">
        <div className={`${isLoading ? "d-none" : ""}`}>
          {!!data ? (
            children || <Outlet />
          ) : (
            <div className="w-100 h-100 d-flex justify-content-center align-items-center">
              No data found.
            </div>
          )}
        </div>
        {isLoading && (
          <div className="vw-100 vh-100 d-flex justify-content-center align-items-center position-fixed top-0 start-0 z-50">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {/* Toast rendering */}
        {Object.values(ToastPlacement).map((placement) => (
          <div
            key={placement}
            className={`position-absolute z-3 ${toastPlacementClasses[placement]}`}
            style={{ maxWidth: "350px" }}
          >
            <div className="flex flex-col">
              {getToastsByPlacement(placement as ToastPlacement).map(
                (toast) => (
                  <div key={toast.id} className="">
                    <Toast
                      {...toast}
                      onClose={() =>
                        setToasts((ts) => ts.filter((t) => t.id !== toast.id))
                      }
                    />
                  </div>
                )
              )}
            </div>
          </div>
        ))}
        {/* End toast rendering */}
      </div>
    </LoadingOverlayContext.Provider>
  );
};
